import { Controller } from '@hotwired/stimulus';

// The notify line on home. Two rules, and they are the whole design:
//
//   Nothing is asked on load. The browser's permission prompt only opens
//   from a click — an unasked-for prompt is the cookie banner, and people
//   dismiss it the same way, forever.
//
//   It asks once. A "no" to the browser is permanent (there is no second
//   prompt), and a "not now" to us is kept in localStorage. Either way the
//   line does not come back.
//
// So the resting state is off, said quietly and once, with the way to change
// it right there. Hidden entirely unless there is something to offer: a
// browser that can push, and a permission nobody has answered yet.
//
// Signed out, there is nothing to subscribe — a push subscription belongs to
// an account. Then the same line is an invitation, and the button is a link
// to sign up (signupValue).
export default class extends Controller {
  static targets = ['bar', 'done', 'blocked'];
  static values = { key: String, url: String, signup: String, card: Boolean };

  // Swapped rather than removed: the line is a flex row, and relying on
  // `hidden flex` in the markup means relying on which of the two Tailwind
  // emits last. The card's one more state is a browser that already said
  // no: only its own settings can undo that, so it says how (no button).
  connect() {
    if (this.offerable) this.barTarget.classList.replace('hidden', 'flex');
    else if (this.blocked) this.blockedTarget.classList.remove('hidden');
  }

  // Signed out we only need somewhere to send them. Signed in, everything
  // has to be there: the transport, an unanswered permission, a key to
  // subscribe with, and no "not now" on record.
  get native() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'bridge--push')
      ?.enabled;
  }

  get blocked() {
    return this.hasBlockedTarget && !this.native && window.Notification?.permission === 'denied';
  }

  // The card is the alerts question (fire/hi/_alerts): the person walked to
  // it, and only somebody with no device here is asked it. So it ignores an
  // old "not now", and a permission already granted is one tap, no prompt.
  get offerable() {
    if (this.signupValue) return !this.dismissed;
    if (this.native) return this.cardValue || !this.dismissed;
    if (!('Notification' in window)) return false;

    const answer = Notification.permission;
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      (answer === 'default' || (this.cardValue && answer === 'granted')) &&
      this.keyValue &&
      (this.cardValue || !this.dismissed)
    );
  }

  async ask(event) {
    event.preventDefault();
    if (this.signupValue) return (window.location.href = this.signupValue);

    // Whatever they answer, we asked. Don't ask again.
    this.remember();
    if (this.native) {
      this.application.getControllerForElementAndIdentifier(this.element, 'bridge--push')?.ask();
      return this.settle('done');
    }

    // The bar steps aside at once; the card's button says it is working.
    const button = event.currentTarget;
    if (this.cardValue) this.busy(button, true);
    else this.hide();

    const answer = await Notification.requestPermission();
    if (answer === 'denied') return this.settle('blocked');
    if (answer === 'granted') {
      try {
        await this.subscribe();
        return this.settle('done');
      } catch (e) {
        console.warn('push subscribe failed', e);
      }
    }
    // The prompt closed unanswered, or the post failed: the card asks again.
    if (this.cardValue) this.busy(button, false);
  }

  // The Avisos switch (walk/ME.md screen 1): ON asks, the same ask as the
  // menu's; OFF only saves the choice, the OS keeps its permission.
  toggle(event) {
    if (event.currentTarget.checked) this.ask(event);
  }

  busy(button, on) {
    button.disabled = on;
    button.querySelector('.loading')?.classList.toggle('hidden', !on);
  }

  // One state at a time: the ask goes, the answer shows. A bar has no
  // blocked line, so a "no" there simply leaves nothing behind.
  settle(name) {
    this.hide();
    this.targets.find(name)?.classList.remove('hidden');
  }

  hide() {
    this.barTarget.classList.replace('flex', 'hidden');
  }

  // The service worker is Rails' own, at /service-worker, so its scope is the
  // whole site. Registering is idempotent — the browser keeps one per scope.
  async subscribe() {
    const reg = await navigator.serviceWorker.register('/service-worker');
    await navigator.serviceWorker.ready;

    const sub =
      (await reg.pushManager.getSubscription()) ||
      (await reg.pushManager.subscribe({
        // Required by every browser: a push we don't show is a push we can't send.
        userVisibleOnly: true,
        applicationServerKey: this.vapid,
      }));

    const key = (name) => btoa(String.fromCharCode(...new Uint8Array(sub.getKey(name))));

    const res = await fetch(this.urlValue, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrf },
      body: JSON.stringify({
        // The endpoint is the token here — the push service's own url for
        // this browser. The two keys encrypt the payload to it.
        token: sub.endpoint,
        os: 'web',
        key: { p256dh: key('p256dh'), auth: key('auth') },
      }),
    });
    if (!res.ok) throw new Error(`POST ${this.urlValue}: ${res.status}`);
  }

  // Ours to remember, not the browser's: it has no "asked and dismissed"
  // state, only granted/denied/default. Storage can be off (private windows,
  // blocked site data) — then the line simply shows again, which is the
  // harmless way to be wrong.
  dismiss() {
    this.hide();
    this.remember();
  }

  remember() {
    try {
      localStorage.setItem('notify-asked', '1');
    } catch {
      /* no storage, no memory */
    }
  }

  get dismissed() {
    try {
      return !!localStorage.getItem('notify-asked');
    } catch {
      return false;
    }
  }

  get csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  // VAPID keys travel as url-safe base64; subscribe() wants the bytes.
  get vapid() {
    const b64 = (this.keyValue + '='.repeat((4 - (this.keyValue.length % 4)) % 4))
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  }
}
